import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus } from "lucide-react";
import CustomDropdown, {
  DropdownOption,
} from "../../../../Ui_Components/CustomDropdown";
import {
  ConversionRow,
  UnitConversionData,
  UnitConversionErrors,
  buildBaseConversionRows,
  createId,
} from "../types";
import { getUnitsList } from "../../../../../services/ingredients";

interface UnitConversionStepProps {
  data: UnitConversionData;
  onChange: (data: UnitConversionData) => void;
  errors?: UnitConversionErrors;
  unit?: string;
}

// gm/ml/oz/fl oz already render as the locked base rows, so they're
// excluded from the custom-unit picker to avoid a duplicate entry.
const BASE_UNIT_TYPES = new Set(["gm", "ml", "oz", "fl oz"]);

const UnitConversionStep: React.FC<UnitConversionStepProps> = ({
  data,
  onChange,
  errors = {},
  unit = "",
}) => {
  const rowErrors = errors.rows ?? {};

  // Custom-unit options come from the units master-list API. gm/ml/oz/fl oz
  // are stripped because they're already the locked base rows.
  const [customUnitOptions, setCustomUnitOptions] = useState<DropdownOption[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const units = await getUnitsList();
        if (cancelled) return;
        setCustomUnitOptions(
          units
            .filter((u) => !BASE_UNIT_TYPES.has(u.unit_type))
            .map((u) => ({ value: u.unit_type, label: u.unit_type })),
        );
      } catch (err) {
        if (cancelled) return;
        console.error("[UnitConversionStep] units fetch failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const lastSyncedUnitRef = useRef(unit);
  useEffect(() => {
    if (unit === lastSyncedUnitRef.current) return;
    lastSyncedUnitRef.current = unit;
    if (!unit) return;
    const desired = buildBaseConversionRows(unit);
    const currentLocked = data.rows.filter((r) => r.locked);
    const alreadyMatches =
      currentLocked.length === desired.length &&
      currentLocked.every((r, i) => r.unit === desired[i].unit);
    if (alreadyMatches) return;
    const customRows = data.rows.filter((r) => !r.locked);
    onChange({ ...data, rows: [...desired, ...customRows] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unit]);

  // Saved rows may have unit values with different casing than the master
  // units list (e.g. "piece (small)" vs. "Piece (small)"). CustomDropdown
  // matches values exactly, so normalize row values to the master list's
  // casing once the options arrive — otherwise the dropdown shows the
  // placeholder even though the value is set.
  useEffect(() => {
    if (customUnitOptions.length === 0) return;
    const optionByLower = new Map(
      customUnitOptions.map((opt) => [opt.value.toLowerCase(), opt.value]),
    );
    const needsUpdate = data.rows.some((row) => {
      if (row.locked || !row.unit) return false;
      const canonical = optionByLower.get(row.unit.toLowerCase());
      return canonical !== undefined && canonical !== row.unit;
    });
    if (!needsUpdate) return;
    onChange({
      ...data,
      rows: data.rows.map((row) => {
        if (row.locked || !row.unit) return row;
        const canonical = optionByLower.get(row.unit.toLowerCase());
        return canonical && canonical !== row.unit
          ? { ...row, unit: canonical }
          : row;
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customUnitOptions]);

  const patchRow = (id: string, patch: Partial<ConversionRow>) => {
    onChange({
      ...data,
      rows: data.rows.map((r) => (r.id === id ? { ...r, ...patch } : r)),
    });
  };

  const removeRow = (id: string) => {
    onChange({ ...data, rows: data.rows.filter((r) => r.id !== id) });
  };

  const addRow = () => {
    onChange({
      ...data,
      rows: [
        ...data.rows,
        { id: createId(), locked: false, unit: "", grams: "" },
      ],
    });
  };

  return (
    <div className="pt-4">
      <div className="bg-white rounded-md border border-[#F0F0F0] p-4 sm:p-6">
        <div className="w-full lg:w-1/2 lg:max-w-[720px]">
          <div className="grid grid-cols-[1fr_auto_1fr_40px] items-center gap-3 mb-3">
            <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              Units
            </span>
            <span />
            <span className="text-sm 3xl:text-base font-medium font-inter text-[#1F1F1F]">
              grams/ml
            </span>
            <span />
          </div>

          <div className="flex flex-col gap-3">
            {data.rows.map((row) => {
              const err = rowErrors[row.id] ?? {};
              return (
                <div
                  key={row.id}
                  className="grid grid-cols-[1fr_auto_1fr_40px] items-center gap-3"
                >
                  {row.locked ? (
                    <input
                      type="text"
                      value={row.unit}
                      readOnly
                      className="w-full px-3 py-2 border border-[#E0E0E0] rounded-md bg-[#F9FAFB] text-sm 3xl:text-base font-inter text-[#6B7280]"
                    />
                  ) : (
                    <div>
                      <CustomDropdown
                        options={customUnitOptions}
                        value={row.unit}
                        onChange={(v) => patchRow(row.id, { unit: v })}
                        placeholder="Cup(small)"
                        className={
                          err.unit ? "ring-1 ring-red-400 rounded-md" : ""
                        }
                      />
                      {err.unit && (
                        <p className="mt-1 text-xs text-red-500 font-inter">
                          {err.unit}
                        </p>
                      )}
                    </div>
                  )}

                  <span className="text-sm 3xl:text-base font-inter text-[#6B7280] px-1">
                    =
                  </span>

                  <div>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={row.grams}
                      readOnly={row.locked}
                      onChange={(e) =>
                        patchRow(row.id, {
                          grams: e.target.value.replace(/[^0-9.]/g, ""),
                        })
                      }
                      placeholder="128"
                      className={`w-full px-3 py-2 border rounded-md text-sm 3xl:text-base font-inter outline-none transition-colors ${
                        row.locked
                          ? "bg-[#F9FAFB] text-[#6B7280] border-[#E0E0E0]"
                          : err.grams
                            ? "border-red-400 focus:border-red-500 text-[#1F1F1F]"
                            : "border-[#E0E0E0] focus:border-[#0DB5B5] text-[#1F1F1F]"
                      }`}
                    />
                    {err.grams && (
                      <p className="mt-1 text-xs text-red-500 font-inter">
                        {err.grams}
                      </p>
                    )}
                  </div>

                  <div className="flex items-center justify-center">
                    {!row.locked && (
                      <button
                        type="button"
                        onClick={() => removeRow(row.id)}
                        className="w-9 h-9 rounded-md bg-[#FDECEC] text-[#E53935] flex items-center justify-center hover:bg-[#FBDADA] transition-colors"
                        aria-label="Remove row"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          <button
            type="button"
            onClick={addRow}
            className="mt-4 flex items-center gap-1.5 text-[#0DB5B5] text-sm 3xl:text-base font-medium font-inter hover:text-[#0CA0A0] transition-colors"
          >
            <Plus className="w-4 h-4" />
            Add another
          </button>
        </div>
      </div>
    </div>
  );
};

export default UnitConversionStep;
